/* eslint-disable max-lines */
'use client';

import React, { useRef } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '../ui-kit/badge';
import { Table, TableBody, TableCell, TableRow } from '../ui-kit/table';
import { FloatingActions } from './floating-actions';
import GridMenuDropdown, { MenuOption } from './grid-menu-dropdown';
import { EmptyState } from '../ui-kit/empty-state';
import { Skeleton } from '../ui-kit/skeleton';
import { DataGridColumn, DataGridV2Props } from './data-grid-types';
import { useDataGridTable } from './use-data-grid-table';
import { useDataGridSelection } from './use-data-grid-selection';
import { DataGridTableHead } from './data-grid-table-head';
import { DataGridPagination } from './data-grid-pagination';
import { DataGridConfirmModal } from './data-grid-confirm-modal';
import {
  MobileFilterModal,
  MobileCardView,
  BulkActionsBar
} from './data-grid-mobile';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DataGridV2<T extends Record<string, any>>(props: DataGridV2Props<T>) {
  const {
    data,
    columns,
    enableRowSelection = false,
    onRowClick,
    getRowProps,
    onCellClick,
    renderRowActions,
    enableExpanding = false,
    renderSubComponent,
    getRowCanExpand,
    getRowId,
    height = 'unset',
    fill = false,
    dense = true,
    striped = false,
    grid = false,
    enableFiltering = true,
    manualFiltering = false,
    enablePagination = true,
    manualPagination = false,
    totalItems,
    showColumnFilters = false,
    filterMode = 'popover',
    menuOptions,
    onMenuOptionClick,
    setMenuOptions,
    onDelete,
    onMove,
    isDeleting = false,
    isMoving = false,
    isMerging = false,
    folders,
    currentFolderId,
    selectedToMove,
    setSelectedToMove,
    selectedToMerge,
    setSelectedToMerge,
    actions: actionsProp = [],
    cursor = 'default',
    virtualize = false,
    estimatedRowHeight = 37
  } = props;

  const g = useDataGridTable(props);

  const {
    themeText,
    themeCellText,
    themeLayer,
    themeBorder,
    headerBg,
    footerBg,
    copyHoverBg,
    getColour,
    getBrandTint,
    isMobile,
    columnFilters,
    setColumnFilters,
    rowSelection,
    setRowSelection,
    pagination,
    setPagination,
    expanded,
    containerRef,
    mousePosition,
    setMousePosition,
    isDropdownVisible,
    setDropdownVisible,
    toContainerPos,
    table,
    tableColumns,
    visibleBulkActions,
    execute,
    executeBulk,
    confirmState,
    confirmProceed,
    cancelConfirm,
    commitFilterValue,
    searchParams,
    setSearchParams,
    isPaginating,
    shouldShowLoading,
    shouldShowNoData,
    currentPage,
    totalPages,
    canPreviousPage,
    canNextPage,
    getPageNumbers,
    selectedRowsArray,
    pageSizeOptions,
    t
  } = g;

  const selection = useDataGridSelection(
    table,
    enableRowSelection && !!props.enableRangeSelection
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const allRows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: virtualize ? allRows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 5
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalVirtualHeight = rowVirtualizer.getTotalSize();

  const handleClearAllFilters = () => {
    setColumnFilters([]);
    if (manualFiltering) {
      setSearchParams({
        offset: searchParams?.offset ?? '0',
        limit: searchParams?.limit ?? String(props.pageSize ?? 25),
        q: undefined
      });
      props.onFilteringChange?.([]);
    }
  };

  return (
    <>
      {/* Active filter chips */}
      <AnimatePresence>
        {columnFilters.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="flex items-center justify-between gap-2 flex-wrap pb-2"
          >
            {columnFilters.map(filter => {
              const col = tableColumns.find(c => c.id === filter.id) as
                | DataGridColumn<T>
                | undefined;
              const colName =
                (typeof col?.header === 'string' ? col.header : col?.id) ||
                filter.id;

              return (
                <motion.span
                  key={filter.id}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.12 }}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 text-[11px] rounded-full"
                  style={{
                    backgroundColor: getBrandTint(0.08),
                    color: getColour('activeText'),
                    boxShadow: `0 0 0 1px ${getBrandTint(0.3)}`
                  }}
                >
                  <span className="font-medium">{colName}:</span>
                  <span className="opacity-80 max-w-[120px] truncate">
                    {String(filter.value)}
                  </span>
                  <button
                    onClick={() => commitFilterValue(filter.id, '', true)}
                    className="ml-0.5 rounded-full p-0.5 transition-opacity hover:opacity-60"
                    style={{ color: getColour('activeText') }}
                    aria-label={`${t('remove-filter') || 'Remove filter'}: ${colName}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.span>
              );
            })}

            <button
              onClick={handleClearAllFilters}
              className="inline-flex items-center gap-1 pl-2 pr-2.5 py-1 text-[11px] rounded-full transition-opacity hover:opacity-70 cursor-pointer"
              style={{
                backgroundColor: getColour('dangerSoft'),
                color: getColour('danger'),
                boxShadow: `0 0 0 1px ${getColour('danger')}30`
              }}
            >
              <X className="w-3 h-3" />
              {t('clear-all') || 'Clear all'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={containerRef}
        className={cn(
          'flex flex-col w-full min-w-[350px] rounded-2xl relative shadow-card',
          fill && 'flex-1 min-h-0'
        )}
        style={{
          height: fill ? undefined : height,
          // ``minHeight`` used to be a fixed 400px, which fought with
          // ``fill`` on short viewports: the grid ignored the flex
          // parent's cap and pushed pagination past the fold. With
          // ``fill`` we let the flex parent decide; without it we keep
          // the 400px floor so non-fill callers with a small
          // ``height`` prop don't collapse the grid.
          minHeight: fill ? undefined : '400px',
          backgroundColor: themeLayer,
          border: `1px solid ${themeBorder}`
        }}
        onContextMenu={e => {
          if (menuOptions && menuOptions.length) {
            e.preventDefault();
            setMousePosition({ x: e.clientX, y: e.clientY });
            setDropdownVisible(true);
          }
        }}
      >
        <div
          ref={scrollRef}
          className={cn(
            'flex-1 -mx-2 sm:mx-0 rounded-2xl relative',
            'overflow-x-auto overflow-y-auto'
          )}
        >
          {/* Mobile view */}
          {isMobile ? (
            <>
              {enableFiltering && (
                <MobileFilterModal<T>
                  columns={columns}
                  enableFiltering={enableFiltering}
                  columnFilters={columnFilters}
                  commitFilterValue={commitFilterValue}
                  themeText={themeText}
                  themeLayer={themeLayer}
                  themeBorder={themeBorder}
                />
              )}
              {shouldShowLoading ? (
                <div className="p-4">
                  <Skeleton variant="table" rows={5} />
                </div>
              ) : shouldShowNoData ? (
                <EmptyState title={t('no_data')} />
              ) : (
                <MobileCardView<T>
                  rows={table.getRowModel().rows}
                  columns={tableColumns as DataGridColumn<T>[]}
                  onRowClick={onRowClick}
                  rowSelection={rowSelection}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onRowSelectionChange={(updater: any) => {
                    const next =
                      typeof updater === 'function'
                        ? updater(rowSelection)
                        : updater;
                    setRowSelection(next);
                  }}
                  renderRowActions={renderRowActions}
                  actionsProp={actionsProp}
                  execute={execute}
                  themeText={themeText}
                  themeLayer={themeLayer}
                  themeBorder={themeBorder}
                  enableRowSelection={
                    enableRowSelection ||
                    !!onDelete ||
                    !!onMove ||
                    actionsProp.some(a => !!a.runBulk)
                  }
                />
              )}
            </>
          ) : (
            <Table
              dense={dense}
              striped={striped}
              grid={grid}
              className="
                [--gutter:--spacing(6)] sm:[--gutter:--spacing(8)]
                [&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-2 [&_td]:text-sm
                [&_th:first-child]:!pl-4 [&_th:last-child]:!pr-4
                [&_td:first-child]:!pl-4 [&_td:last-child]:!pr-4
              "
            >
              <DataGridTableHead<T>
                table={table}
                columns={columns}
                showColumnFilters={showColumnFilters}
                enableFiltering={enableFiltering}
                filterMode={filterMode}
                manualFiltering={manualFiltering}
                columnFilters={columnFilters}
                commitFilterValue={commitFilterValue}
                headerBg={headerBg}
                themeBorder={themeBorder}
                getColour={getColour}
              />

              <TableBody>
                {shouldShowLoading ? (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length} className="p-4">
                      <Skeleton variant="table" rows={5} />
                    </TableCell>
                  </TableRow>
                ) : shouldShowNoData ? (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length} className="py-10">
                      <EmptyState title={t('no_data')} />
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {virtualize && virtualItems.length > 0 && (
                      <tr
                        style={{ height: virtualItems[0].start }}
                        aria-hidden
                      />
                    )}
                    {(virtualize
                      ? virtualItems.map(vi => allRows[vi.index])
                      : allRows
                    ).map(row => {
                      // The virtual spacer <tr> is always :nth-child(1) when present,
                      // shifting data rows by 1 in CSS :nth-child counting. Without
                      // the spacer, row.index=0 is :nth-child(1) (odd = not striped).
                      // With the spacer, row.index=0 is :nth-child(2) (even = striped).
                      const hasVirtualSpacer =
                        virtualize && virtualItems.length > 0;
                      const isCssStripedRow = hasVirtualSpacer
                        ? row.index % 2 === 0
                        : row.index % 2 === 1;
                      const isExpanded = expanded[row.id];
                      const rowProps = getRowProps?.(
                        row.original,
                        selectedRowsArray
                      );
                      const canExpand =
                        enableExpanding &&
                        renderSubComponent &&
                        (getRowCanExpand
                          ? getRowCanExpand(row.original)
                          : true);

                      return (
                        <React.Fragment key={row.id}>
                          <TableRow
                            {...rowProps}
                            aria-selected={
                              enableRowSelection
                                ? row.getIsSelected()
                                : undefined
                            }
                            className={cn('group', rowProps?.className)}
                            onClickCapture={event => {
                              rowProps?.onClickCapture?.(event);
                              if (!event.defaultPrevented)
                                selection.onClickCapture(event, row);
                            }}
                            onMouseDown={event => {
                              const selected = event.shiftKey
                                ? selection.getDragSelection(event, row)
                                : undefined;
                              const pointerProps = selected
                                ? getRowProps?.(row.original, selected)
                                : rowProps;
                              pointerProps?.onMouseDown?.(event);
                            }}
                            onDragStart={event => {
                              const selected = selection.getDragSelection(
                                event,
                                row
                              );
                              const dragProps = selected
                                ? getRowProps?.(row.original, selected)
                                : rowProps;
                              dragProps?.onDragStart?.(event);
                            }}
                            onClick={e => {
                              rowProps?.onClick?.(e);
                              if (!e.defaultPrevented)
                                onRowClick?.(row.original);
                            }}
                            onContextMenu={e => {
                              rowProps?.onContextMenu?.(e);
                              if (e.defaultPrevented) return;
                              e.preventDefault();
                              e.stopPropagation();
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              setMenuOptions?.(row.original as any);
                              const pos = toContainerPos(e.clientX, e.clientY);
                              setMousePosition(pos);
                              setDropdownVisible(true);
                            }}
                            style={{
                              color: themeCellText,
                              cursor,
                              userSelect: props.enableRangeSelection
                                ? 'none'
                                : undefined,
                              ...rowProps?.style
                            }}
                            onMouseEnter={e => {
                              rowProps?.onMouseEnter?.(e);
                              const tr = e.currentTarget as HTMLTableRowElement;
                              const hoverBg =
                                rowProps?.style?.backgroundColor ??
                                getBrandTint(0.04);
                              tr.style.backgroundColor = hoverBg;
                              const sticky = tr.querySelector<HTMLElement>(
                                '[data-sticky-actions]'
                              );
                              if (sticky) sticky.style.background = hoverBg;
                            }}
                            onMouseLeave={e => {
                              rowProps?.onMouseLeave?.(e);
                              const tr = e.currentTarget as HTMLTableRowElement;
                              tr.style.backgroundColor =
                                rowProps?.style?.backgroundColor ?? '';
                              const sticky = tr.querySelector<HTMLElement>(
                                '[data-sticky-actions]'
                              );
                              if (sticky)
                                sticky.style.background =
                                  rowProps?.style?.backgroundColor ??
                                  (isCssStripedRow
                                    ? 'linear-gradient(var(--tbl-stripe-even), var(--tbl-stripe-even)), var(--tbl-bg)'
                                    : 'var(--tbl-bg)');
                            }}
                          >
                            {row.getVisibleCells().map(cell => {
                              const columnDef = cell.column
                                .columnDef as DataGridColumn<T>;
                              const field =
                                columnDef.field || columnDef.accessorKey;
                              const isActionsCell =
                                cell.column.id === 'actions';

                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              const rawClamp = (columnDef as any).clampLines;
                              const effectiveClampLines =
                                typeof rawClamp === 'number' ? rawClamp : 2;

                              const clampStyle:
                                | React.CSSProperties
                                | undefined =
                                typeof effectiveClampLines === 'number' &&
                                effectiveClampLines > 0
                                  ? (() => {
                                      const s: React.CSSProperties = {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        display: '-webkit-box' as any
                                      };
                                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                      (s as any).WebkitLineClamp =
                                        effectiveClampLines;
                                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                      (s as any).WebkitBoxOrient = 'vertical';
                                      return s;
                                    })()
                                  : undefined;

                              const rawForTitle = columnDef.suppressCellTitle
                                ? undefined
                                : (field &&
                                    row.original[field as keyof T] !==
                                      undefined &&
                                    row.original[field as keyof T] !== null &&
                                    typeof row.original[field as keyof T] !==
                                      'object' &&
                                    String(row.original[field as keyof T])) ||
                                  undefined;

                              return (
                                <TableCell
                                  key={cell.id}
                                  {...(cell.column.id === 'select' && {
                                    'data-row-select': ''
                                  })}
                                  {...(isActionsCell && {
                                    'data-sticky-actions': ''
                                  })}
                                  style={{
                                    width:
                                      cell.column.getSize() !== 150
                                        ? cell.column.getSize()
                                        : undefined,
                                    minWidth: cell.column.columnDef.minSize,
                                    maxWidth: cell.column.columnDef.maxSize,
                                    color: themeCellText,
                                    borderColor: themeBorder,
                                    ...(isActionsCell && {
                                      position: 'sticky',
                                      right: 0,
                                      zIndex: 9,
                                      background: isCssStripedRow
                                        ? 'linear-gradient(var(--tbl-stripe-even), var(--tbl-stripe-even)), var(--tbl-bg)'
                                        : 'var(--tbl-bg)',
                                      boxShadow:
                                        '-4px 0 8px -4px rgba(0,0,0,0.08)'
                                    })
                                  }}
                                  onClick={async e => {
                                    if (isActionsCell) return;
                                    if (onCellClick && field) {
                                      e.stopPropagation();
                                      onCellClick(row.original, String(field));
                                    }
                                    if (
                                      columnDef.enableRowCopyOnClick &&
                                      field
                                    ) {
                                      e.stopPropagation();
                                      const raw =
                                        row.original[field as keyof T];
                                      if (raw !== undefined && raw !== null) {
                                        try {
                                          await navigator.clipboard.writeText(
                                            String(raw)
                                          );
                                          toast.success(
                                            t('copied-to-clipboard')
                                          );
                                        } catch {
                                          toast.error(t('failed-to-copy'));
                                        }
                                      }
                                    }
                                  }}
                                  onMouseEnter={
                                    columnDef.enableRowCopyOnClick
                                      ? e => {
                                          e.currentTarget.style.backgroundColor =
                                            copyHoverBg;
                                        }
                                      : undefined
                                  }
                                  onMouseLeave={
                                    columnDef.enableRowCopyOnClick
                                      ? e => {
                                          e.currentTarget.style.backgroundColor =
                                            '';
                                        }
                                      : undefined
                                  }
                                  className={cn(
                                    '!px-1 align-middle',
                                    columnDef.enableRowCopyOnClick === true &&
                                      'cursor-pointer'
                                  )}
                                  title={rawForTitle}
                                >
                                  {clampStyle ? (
                                    <div style={clampStyle}>
                                      {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                      )}
                                    </div>
                                  ) : (
                                    flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>

                          {canExpand && (
                            <AnimatePresence>
                              {isExpanded && (
                                <tr>
                                  <td
                                    colSpan={row.getVisibleCells().length}
                                    className="p-0"
                                  >
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      transition={{
                                        duration: 0.2,
                                        ease: 'easeInOut'
                                      }}
                                      style={{ overflow: 'hidden' }}
                                    >
                                      <div
                                        className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t"
                                        style={{
                                          color: themeText,
                                          borderColor: themeBorder
                                        }}
                                      >
                                        {renderSubComponent!(row.original)}
                                      </div>
                                    </motion.div>
                                  </td>
                                </tr>
                              )}
                            </AnimatePresence>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {virtualize &&
                      totalVirtualHeight > 0 &&
                      virtualItems.length > 0 && (
                        <tr
                          style={{
                            height:
                              totalVirtualHeight -
                              (virtualItems[virtualItems.length - 1].end ?? 0)
                          }}
                          aria-hidden
                        />
                      )}
                  </>
                )}
              </TableBody>
            </Table>
          )}

          {/* Paging overlay */}
          {isPaginating && (
            <div className="pointer-events-none absolute inset-0 bg-white/40 dark:bg-black/20 backdrop-blur-[1px] flex items-center justify-center">
              <div className="inline-block h-3 w-3 animate-spin rounded-full border border-zinc-400 border-t-transparent" />
            </div>
          )}
        </div>

        <DataGridPagination
          enablePagination={enablePagination}
          table={table}
          pagination={pagination}
          setPagination={setPagination}
          totalItems={totalItems}
          data={data}
          manualPagination={manualPagination}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          pageSizeOptions={pageSizeOptions}
          isPaginating={isPaginating}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          currentPage={currentPage}
          totalPages={totalPages}
          getPageNumbers={getPageNumbers}
          themeText={themeText}
          themeLayer={themeLayer}
          themeBorder={themeBorder}
          footerBg={footerBg}
          getColour={getColour}
          getBrandTint={getBrandTint}
          t={t}
        />

        <BulkActionsBar<T>
          selected={selectedRowsArray}
          getBulkActions={visibleBulkActions}
          onExecuteBulk={(rows, actionId) => {
            const action = actionsProp.find(a => a.id === actionId);
            if (action) executeBulk(action, rows);
          }}
          themeLayer={themeLayer}
          themeBorder={themeBorder}
        />

        {(onDelete || onMove) && Object.keys(rowSelection).length > 0 && (
          <FloatingActions
            gridApi={
              {
                getSelectedNodes: () => {
                  const idToRow = new Map<string, T>();
                  data.forEach((row, idx) => {
                    const id =
                      typeof getRowId === 'function'
                        ? String(getRowId(row, idx))
                        : String(idx);
                    idToRow.set(id, row);
                  });
                  return Object.entries(rowSelection)
                    .filter(([, isSelected]) => isSelected)
                    .map(([id]) => {
                      const row = idToRow.get(String(id));
                      return row ? { data: row } : null;
                    })
                    .filter((n): n is { data: T } => !!n);
                },
                getRenderedNodes: () => data.map(row => ({ data: row })),
                deselectAll: () => setRowSelection({}),
                setServerSideSelectionState: () => {}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            selectedState={selectedRowsArray}
            deleteRows={onDelete}
            moveRows={onMove}
            isDeleting={isDeleting}
            isMoving={isMoving}
            isMerging={isMerging}
            folders={folders}
            currentFolderId={currentFolderId}
            selectedToMove={selectedToMove}
            canMoveToFolder={props.canMoveToFolder}
            setSelectedToMove={setSelectedToMove}
            selectedToMerge={selectedToMerge}
            setSelectedToMerge={setSelectedToMerge}
          />
        )}

        {menuOptions && menuOptions.length > 0 && (
          <GridMenuDropdown
            isDropdownVisible={isDropdownVisible}
            setDropdownVisible={setDropdownVisible}
            mousePosition={mousePosition}
            menuOptions={menuOptions}
            onMenuOptionClick={onMenuOptionClick}
          />
        )}

        <DataGridConfirmModal
          confirmState={confirmState}
          confirmProceed={confirmProceed}
          cancelConfirm={cancelConfirm}
          themeBorder={themeBorder}
          t={t}
        />
      </div>
    </>
  );
}

export { Badge };
export type { MenuOption };
export type { DataGridColumn };
export default DataGridV2;
